// src/firebase.js
// Purpose: Initialize Firebase for both browser (React app) and Node (CLI seeders).
// Safe to use in the Firebase Emulator, staging, or production.

import { initializeApp } from "firebase/app";
import {
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  connectFirestoreEmulator,
} from "firebase/firestore";
import { getStorage, connectStorageEmulator } from "firebase/storage";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";

// -----------------------------------------------------------------------------
// Dynamic imports for browser-only modules (Auth + Analytics)
// -----------------------------------------------------------------------------
let getAuth, connectAuthEmulator, getAnalytics;
if (typeof window !== "undefined") {
  const authModule = await import("firebase/auth");
  getAuth = authModule.getAuth;
  connectAuthEmulator = authModule.connectAuthEmulator;

  const analyticsModule = await import("firebase/analytics");
  getAnalytics = analyticsModule.getAnalytics;
}

// -----------------------------------------------------------------------------
// Firebase configuration
// -----------------------------------------------------------------------------
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// Initialize core Firebase app
const app = initializeApp(firebaseConfig);

// -----------------------------------------------------------------------------
// Firestore initialization (browser vs Node)
// -----------------------------------------------------------------------------
const db =
  typeof window === "undefined"
    ? getFirestore(app)
    : initializeFirestore(app, {
      localCache: persistentLocalCache({
        tabManager: persistentMultipleTabManager(),
      }),
    });
const studioDb =
  typeof window === "undefined"
    ? getFirestore(app, "studio")
    : initializeFirestore(
      app,
      {
        localCache: persistentLocalCache({
          tabManager: persistentMultipleTabManager(),
        }),
      },
      "studio"
    );

// -----------------------------------------------------------------------------
// Other Firebase services
// -----------------------------------------------------------------------------
const storage = getStorage(app);
const functionsRegion = process.env.REACT_APP_FIREBASE_FUNCTIONS_REGION || "us-central1";
const functions = getFunctions(app, functionsRegion);

// Auth and Analytics (browser only)
const auth = typeof window !== "undefined" && getAuth ? getAuth(app) : null;
let analytics = null;
try {
  const isLocal = typeof window !== "undefined" && window.location.hostname === "localhost";
  if (firebaseConfig.measurementId && typeof window !== "undefined" && !isLocal) {
    analytics = getAnalytics(app);
  }
} catch {
  // No-op for SSR or unsupported browsers
}

// -----------------------------------------------------------------------------
// 🔧 Emulator configuration
// -----------------------------------------------------------------------------
const useEmulators = false;

const disableAuthEmulator =
  (process.env.REACT_APP_DISABLE_AUTH_EMULATOR || "").toLowerCase() === "true";

const EMU_HOST = process.env.REACT_APP_EMU_HOST || "127.0.0.1";
const EMU_PORTS = {
  auth: Number(process.env.REACT_APP_EMU_AUTH_PORT || 9099),
  firestore: Number(process.env.REACT_APP_EMU_FIRESTORE_PORT || 8080),
  storage: Number(process.env.REACT_APP_EMU_STORAGE_PORT || 9199),
  functions: Number(process.env.REACT_APP_EMU_FUNCTIONS_PORT || 5001),
};

// Connect to emulators (works for both browser and Node)
if (useEmulators) {
  try {
    connectFirestoreEmulator(db, EMU_HOST, EMU_PORTS.firestore);
    connectStorageEmulator(storage, EMU_HOST, EMU_PORTS.storage);
    connectFunctionsEmulator(functions, EMU_HOST, EMU_PORTS.functions);

    if (!disableAuthEmulator && auth && connectAuthEmulator) {
      connectAuthEmulator(auth, `http://${EMU_HOST}:${EMU_PORTS.auth}`, {
        disableWarnings: true,
      });
    }

    if (process.env.NODE_ENV !== "test") {
      console.log(
        `[Firebase] Connected to emulators: firestore:${EMU_PORTS.firestore}, storage:${EMU_PORTS.storage}, functions:${EMU_PORTS.functions}${disableAuthEmulator ? " (Auth skipped)" : `, auth:${EMU_PORTS.auth}`}`
      );
    }
  } catch (e) {
    console.error("[Firebase] Emulator connection failed:", e);
  }
}

// -----------------------------------------------------------------------------
// Exports
// -----------------------------------------------------------------------------
export { app, db, studioDb, auth, storage, functions, analytics, useEmulators };
